<template lang="pug">
section.message-thread
  template(v-if="messages.length === 0")
    article.message
      h3.message-heading
        | No messages to display
  template(
    v-if="messages.length > 0"
    v-for="message in messages")
    article.message(
      v-if="coachProfiles && coachProfiles[message.coachId]"
      :class=`[{
          alt: !isMessageFromCoach(message) && message.type !== 'SYSTEM',
          'has-supervisor-review': message.sentOn < supervisorReviewDate
        },
        'type-' + messageTypeToClass(message.type)]`)
      //- Coach message
      h3.message-heading(
        v-if="isMessageFromCoach(message)")
        | Coach {{ coachProfiles[message.coachId]['name'] }} &nbsp;
        small {{ getHumanReadableMessageTimestamp(message.sentOn) }}
      //- User message
      h3.message-heading(
        v-else)
        template(v-if="message.type === 'SYSTEM'")
          | System message to&nbsp;
        | {{ patient.firstName }} {{ patient.lastName }}
        small &nbsp;{{ getHumanReadableMessageTimestamp(message.sentOn) }}

      p.user-message(
        v-html="getFilteredMessageText(message.message)")

      //- Message attachments
      div.message-attachments(v-if="message.attachments")
        p.message-attachment(
          v-for="attachment in message.attachments"
          :class="'attachment-type-' + attachment.type.toLowerCase()")
          template(v-if="attachment.type === 'ACTIVITY' && attachment.attachmentMeta")
            div.coaching-message-activity-link
              a(
                :href="buildActivityURL(attachment.content, attachment.attachmentMeta.attachmentType)"
                target="_blank")
                img(
                  v-bind:src="attachment.attachmentMeta.imageURL"
                  v-bind:alt="attachment.attachmentMeta.title")
                h4 {{ attachment.attachmentMeta.title }}
          template(v-else-if="attachment.type === 'INTERNAL_LINK'")
            attachment-internal-link(:attachment="attachment")
          template(v-else)
            a.link-highlight(
              :href="encodeURI(attachment.content)"
              target="_blank")
              | {{ attachment.attachmentMeta && attachment.attachmentMeta.title ? attachment.attachmentMeta.title : attachment.content }}

    template(v-if="isMessageFromCoach(message)")
      .message-status(v-if="message.readOn !== null")
        p
          strong Read&nbsp;
          | {{ getHumanReadableMessageTimestamp(message.readOn) }}
      .message-status(v-else)
        p
          strong Unread
</template>
<script>
import AttachmentInternalLink from '@/components/coaching/coachingChat/AttachmentInternalLink';

import auth from '@/services/authSvc.js';
import { mapGetters } from 'vuex';

import moment from 'moment';

export default {
  name: 'MessageThread',
  props: ['messages', 'coach', 'patient', 'supervisorReviewDate'],
  data() {
    return {
      coachProfiles: null
    };
  },
  components: {
    AttachmentInternalLink
  },
  computed: {
    ...mapGetters(['coaches'])
  },
  methods: {
    isMessageFromCoach(messageData) {
      return messageData.type === 'FROM_COACH';
    },
    getHumanReadableMessageTimestamp(unixTime) {
      return moment(unixTime).format('MMMM Do, YYYY h:mm a');
    },
    getFilteredMessageText(text) {
      let filteredText = this.$options.filters.newLineToHtml(text);
      return this.$options.filters.entityToText(filteredText);
    },
    buildActivityURL(activityString, attachmentType) {
      return `${auth.getWebuiUrl()}/activity/${activityString}`;
    },
    setCoachProfiles() {
      this.coachProfiles = this.coaches.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
    },
    underscoreToKebabCase(string) {
      return string.replace(/_/gim, '-');
    },
    messageTypeToClass(string) {
      return this.underscoreToKebabCase(string.toLowerCase());
    }
  },
  filters: {
    newLineToHtml(string) {
      return string.replace(/(?:\r\n|\r|\n)/g, '<br>');
    }
  },
  watch: {
    coaches() {
      this.setCoachProfiles();
    }
  },
  mounted() {
    this.setCoachProfiles();
  }
};
</script>
<style lang="scss" scoped>
section.message-thread {
  overflow: hidden;

  .message {
    @include drop-shadow;
    margin: 0 auto $small-spacing;
    padding: $small-spacing;
    background: #fff;
    border-radius: 6px;
    border-bottom-right-radius: 0;

    &.alt {
      border-bottom-right-radius: 6px;
      border-bottom-left-radius: 0;
      background: #777;
      color: #fff;

      .message-heading small {
        color: #fff;
      }
    }
    &.has-supervisor-review {
      background-color: $supervisor-review-background;

      &.alt,
      .message-heading small {
        color: #666;
      }
    }
    &.type-system {
      background-color: #a6e7ff;
      border: 0px solid #fff;
      display: flex;
      align-items: center;
      align-content: center;
      flex-wrap: wrap;

      .message-heading {
        flex-basis: 100%;
      }

      .icon-wrapper {
        display: inline-block;
        margin-right: 20px;
        height: auto;

        .svgicon {
          display: block;
          height: 36px;
          width: 36px;
        }
      }

      p {
        margin: 0;
        color: #325377;
      }
      .coaching-message-footer {
        display: none;
      }
    }
    .message-heading {
      @include zero;
      font-size: $base-font-size;
      font-weight: $base-font-weight-strong;
      margin-bottom: 0.25em;

      small {
        margin-left: 0.2em;
        display: inline-block;
        font-size: $ms-down1;
        font-weight: $base-font-weight;
        color: $grey;
      }
    }
    .user-message {
      @include zero;
      line-height: 1.25;
    }
    .message-attachments {
      margin-top: 20px;

      .message-attachment {
        &.attachment-type-activity a {
          word-break: break-word;
        }

        a {
          word-break: break-all;
        }
      }
    }
    .coaching-message-activity-link {
      background: $md-grey-100;
      margin: $small-spacing auto;

      a {
        display: flex;
        flex-flow: row nowrap;
        padding-right: $small-spacing;
      }
      img {
        display: inline-block;
        margin-right: $small-spacing;
        width: auto;
        height: 60px;
        align-self: center;
      }
      h4 {
        @include zero;

        width: auto;
        font-size: 0.82em;
        display: inline-block;
        vertical-align: middle;
        align-self: center;
        margin: 10px 0;
      }
    }
  }
  .message-status {
    margin: -$small-spacing auto $small-spacing;
    text-align: right;
    font-size: $ms-down1;
    color: $grey;

    strong {
      font-weight: $base-font-weight-strong;
      text-transform: uppercase;
    }
  }
}
</style>
